import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ShoppingCart,
  Person,
  Menu as MenuIcon,
  Store,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { COLORS, withOpacity } from '../../theme/colors';

const Navbar = () => {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [servicesMenuAnchor, setServicesMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { user, userData, logout, isAdmin } = useAuth();
  const { getCartItemCount } = useCart();

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleServicesMenuOpen = (event) => {
    setServicesMenuAnchor(event.currentTarget);
  };

  const handleServicesMenuClose = () => {
    setServicesMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Products', path: '/products' },
    { label: 'Services', path: null, hasDropdown: true }, // null path indicates dropdown
    { label: 'About', path: '/about' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  const servicesMenuItems = [
    { label: '3D Printing', path: '/services/3d-printing' },
    { label: '3D Design and Prototyping', path: '/services/3d-design-prototyping' },
  ];

  const userMenuItems = user ? [
    { label: 'My Account', path: '/profile' },
    { label: 'My Orders', path: '/orders' },
    { label: 'My Quotes', path: '/my-quotes' },
    ...(isAdmin() ? [{ label: 'Admin Dashboard', path: '/admin' }] : []),
    { label: 'Logout', action: handleLogout },
  ] : [
    { label: 'Login', path: '/login' },
    { label: 'Register', path: '/register' },
  ];

  const MobileDrawer = () => (
  <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      PaperProps={{
        sx: {
      background: `linear-gradient(135deg, ${COLORS.primary.main} 0%, ${COLORS.primary.light} 100%)`,
      color: COLORS.common.white,
          width: 280,
        }
      }}
    >
      <Box sx={{ pt: 3, pb: 2, px: 2 }}>
        {/* Mobile Logo */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ 
            mb: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <img
            src="/images/The-Random-Store---Logo---Transparent - Copy.png"
            alt="The Random Store Logo"
            style={{
              width: 28,
              height: 28,
              marginRight: 12,
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)' // Make logo white for dark drawer
            }}
          />
        </Box>
        
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            item.hasDropdown ? (
              <Box key={item.label}>
                <ListItem
                  sx={{
                    mb: 1,
                    borderRadius: '12px',
                    mx: 1,
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                  />
                </ListItem>
                {servicesMenuItems.map((serviceItem) => (
                  <ListItem
                    button
                    key={serviceItem.label}
                    onClick={() => {
                      navigate(serviceItem.path);
                      setMobileDrawerOpen(false);
                    }}
                    sx={{
                      mb: 1,
                      borderRadius: '12px',
                      mx: 2,
                      ml: 3,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        transform: 'translateX(8px)',
                      }
                    }}
                  >
                    <ListItemText 
                      primary={serviceItem.label}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                      }}
                    />
                  </ListItem>
                ))}
              </Box>
            ) : (
              <ListItem
                button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setMobileDrawerOpen(false);
                }}
                sx={{
                  mb: 1,
                  borderRadius: '12px',
                  mx: 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(8px)',
                  }
                }}
              >
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </ListItem>
            )
          ))}
          
          {user && (
            <>
              <ListItem
                button
                onClick={() => {
                  navigate('/cart');
                  setMobileDrawerOpen(false);
                }}
                sx={{
                  mb: 1,
                  borderRadius: '12px',
                  mx: 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(8px)',
                  }
                }}
              >
                <ListItemText 
                  primary={`Cart (${getCartItemCount()})`}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </ListItem>
              
              {/* User section separator */}
              <Box sx={{ 
                my: 2, 
                mx: 2, 
                height: '1px', 
                background: 'rgba(255,255,255,0.2)' 
              }} />
              
              {userMenuItems.map((item) => (
                <ListItem
                  button
                  key={item.label}
                  onClick={() => {
                    if (item.action) {
                      item.action();
                    } else {
                      navigate(item.path);
                    }
                    setMobileDrawerOpen(false);
                  }}
                  sx={{
                    mb: 1,
                    borderRadius: '12px',
                    mx: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateX(8px)',
                    }
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                  />
                </ListItem>
              ))}
            </>
          )}
          
          {!user && (
            <>
              <Box sx={{ 
                my: 2, 
                mx: 2, 
                height: '1px', 
                background: 'rgba(255,255,255,0.2)' 
              }} />
              
              <ListItem
                button
                onClick={() => {
                  navigate('/login');
                  setMobileDrawerOpen(false);
                }}
                sx={{
                  mb: 1,
                  borderRadius: '12px',
                  mx: 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(8px)',
                  }
                }}
              >
                <ListItemText 
                  primary="Login"
                  primaryTypographyProps={{
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  navigate('/register');
                  setMobileDrawerOpen(false);
                }}
                sx={{
                  mb: 1,
                  borderRadius: '12px',
                  mx: 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(8px)',
                  }
                }}
              >
                <ListItemText 
                  primary="Register"
                  primaryTypographyProps={{
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{
      bgcolor: COLORS.background.paper,
      color: COLORS.text.primary,
      borderBottom: `1px solid ${withOpacity(COLORS.primary.main,0.1)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
        radial-gradient(circle at 20% 50%, ${withOpacity(COLORS.primary.main,0.02)} 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, ${withOpacity(COLORS.primary.main,0.03)} 0%, transparent 50%),
        linear-gradient(45deg, transparent 40%, ${withOpacity(COLORS.primary.main,0.02)} 50%, transparent 60%)
            `,
            zIndex: 1,
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ px: { xs: 0, sm: 2 }, position: 'relative', zIndex: 2 }}>
            {/* Logo */}
            <Box
              display="flex"
              alignItems="center"
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
              onClick={() => navigate('/')}
            >
              {/* Actual logo image */}
              <img
                src="/images/The-Random-Store---Logo---Transparent - Copy.png"
                alt="The Random Store Logo"
                style={{
                  height: 50,
                  marginRight: 12,
                  objectFit: 'contain'
                }}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {isMobile ? (
              <Box display="flex" alignItems="center" gap={1}>
                {/* Cart Icon */}
                <IconButton
                  onClick={() => navigate('/cart')}
                  aria-label={`Shopping cart with ${getCartItemCount()} items`}
                  sx={{
                    color: COLORS.text.secondary,
                    bgcolor: withOpacity(COLORS.primary.main,0.1),
                    borderRadius: '12px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: withOpacity(COLORS.primary.main,0.2),
                      color: COLORS.primary.main,
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <Badge 
                    badgeContent={getCartItemCount()} 
                    color="secondary"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: COLORS.secondary?.main || COLORS.accent?.main || COLORS.error.main,
                        color: COLORS.common.white,
                        fontWeight: 'bold',
                      }
                    }}
                  >
                    <ShoppingCart />
                  </Badge>
                </IconButton>
                {/* User/Profile Icon */}
                <IconButton
                  onClick={handleUserMenuOpen}
                  aria-label={isAdmin() ? "Admin panel settings" : "User profile menu"}
                  sx={{
                    color: COLORS.text.secondary,
                    bgcolor: withOpacity(COLORS.primary.main,0.1),
                    borderRadius: '12px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: withOpacity(COLORS.primary.main,0.2),
                      color: COLORS.primary.main,
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  {isAdmin() ? <AdminPanelSettings /> : <Person />}
                </IconButton>
                {/* Hamburger Menu on the right */}
                <IconButton
                  onClick={() => setMobileDrawerOpen(true)}
                  aria-label="Open navigation menu"
                  sx={{
                    ml: 1,
                    color: COLORS.text.secondary,
                    bgcolor: withOpacity(COLORS.primary.main,0.1),
                    borderRadius: '12px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: withOpacity(COLORS.primary.main,0.2),
                      color: COLORS.primary.main,
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: COLORS.background.default,
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: `1px solid ${withOpacity(COLORS.primary.main,0.1)}`,
                      mt: 1,
                    }
                  }}
                >
                  {user && (
                    <MenuItem 
                      disabled
                      sx={{
                        bgcolor: COLORS.primary.main,
                        color: COLORS.common.white,
                        '&.Mui-disabled': {
                          opacity: 1,
                          color: COLORS.common.white,
                        }
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {userData?.displayName || user.email}
                      </Typography>
                    </MenuItem>
                  )}
                  {userMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={() => {
                        if (item.action) {
                          item.action();
                        } else {
                          navigate(item.path);
                          handleUserMenuClose();
                        }
                      }}
                      sx={{
                        py: 1,
                        px: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: withOpacity(COLORS.primary.main,0.1),
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                {menuItems.map((item) => (
                  item.hasDropdown ? (
                    <Box key={item.label} sx={{ position: 'relative' }}>
                      <Button
                        onClick={handleServicesMenuOpen}
                        onMouseEnter={handleServicesMenuOpen}
                        sx={{
                          color: COLORS.text.primary,
                          fontWeight: location.pathname.startsWith('/services') ? 600 : 500,
                          borderBottom: location.pathname.startsWith('/services') ? 
                            `2px solid ${COLORS.primary.main}` : 'none',
                          px: 2,
                          py: 1,
                          borderRadius: '8px',
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            bgcolor: withOpacity(COLORS.primary.main,0.1),
                            color: COLORS.primary.main,
                            transform: 'translateY(-1px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: `linear-gradient(90deg, transparent, ${withOpacity(COLORS.primary.main,0.2)}, transparent)`,
                              animation: 'shimmer 0.8s ease-in-out',
                            }
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={servicesMenuAnchor}
                        open={Boolean(servicesMenuAnchor)}
                        onClose={handleServicesMenuClose}
                        MenuListProps={{
                          onMouseLeave: handleServicesMenuClose,
                        }}
                        PaperProps={{
                          sx: {
                            bgcolor: COLORS.background.paper,
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: `1px solid ${withOpacity(COLORS.primary.main,0.1)}`,
                            mt: 1,
                            minWidth: 200,
                          }
                        }}
                        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      >
                        {servicesMenuItems.map((serviceItem) => (
                          <MenuItem
                            key={serviceItem.label}
                            onClick={() => {
                              navigate(serviceItem.path);
                              handleServicesMenuClose();
                            }}
                            sx={{
                              py: 1.5,
                              px: 2,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                bgcolor: withOpacity(COLORS.primary.main,0.1),
                                transform: 'translateX(4px)',
                              }
                            }}
                          >
                            {serviceItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  ) : (
                    <Button
                      key={item.label}
                      onClick={() => navigate(item.path)}
                      sx={{
                        color: COLORS.text.primary,
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        borderBottom: location.pathname === item.path ? 
                          `2px solid ${COLORS.primary.main}` : 'none',
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          bgcolor: withOpacity(COLORS.primary.main,0.1),
                          color: COLORS.primary.main,
                          transform: 'translateY(-1px)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: `linear-gradient(90deg, transparent, ${withOpacity(COLORS.primary.main,0.2)}, transparent)`,
                            animation: 'shimmer 0.8s ease-in-out',
                          }
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                ))}
                {/* Cart Icon */}
                <IconButton
                  onClick={() => navigate('/cart')}
                  aria-label={`Shopping cart with ${getCartItemCount()} items`}
                  sx={{
                    color: COLORS.text.secondary,
                    bgcolor: withOpacity(COLORS.primary.main,0.1),
                    borderRadius: '12px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: withOpacity(COLORS.primary.main,0.2),
                      color: COLORS.primary.main,
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <Badge 
                    badgeContent={getCartItemCount()} 
                    color="secondary"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: COLORS.secondary?.main || COLORS.accent?.main || COLORS.error.main,
                        color: COLORS.common.white,
                        fontWeight: 'bold',
                      }
                    }}
                  >
                    <ShoppingCart />
                  </Badge>
                </IconButton>
                {/* User Menu */}
                <IconButton
                  onClick={handleUserMenuOpen}
                  aria-label={isAdmin() ? "Admin panel settings" : "User profile menu"}
                  sx={{
                    color: COLORS.text.secondary,
                    bgcolor: withOpacity(COLORS.primary.main,0.1),
                    borderRadius: '12px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: withOpacity(COLORS.primary.main,0.2),
                      color: COLORS.primary.main,
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  {isAdmin() ? <AdminPanelSettings /> : <Person />}
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: COLORS.background.default,
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: `1px solid ${withOpacity(COLORS.primary.main,0.1)}`,
                      mt: 1,
                    }
                  }}
                >
                  {user && (
                    <MenuItem 
                      disabled
                      sx={{
                        bgcolor: COLORS.primary.main,
                        color: COLORS.common.white,
                        '&.Mui-disabled': {
                          opacity: 1,
                          color: COLORS.common.white,
                        }
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {userData?.displayName || user.email}
                      </Typography>
                    </MenuItem>
                  )}
                  {userMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={() => {
                        if (item.action) {
                          item.action();
                        } else {
                          navigate(item.path);
                          handleUserMenuClose();
                        }
                      }}
                      sx={{
                        py: 1,
                        px: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: withOpacity(COLORS.primary.main,0.1),
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <MobileDrawer />
      
      {/* Add CSS keyframes for shimmer animation */}
      <style>
        {`
          @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>
    </>
  );
};

export default Navbar;
